import { ChildProps } from '@apollo/client/react/hoc';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { Location } from 'history';

import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { US } from '@reverbdotcom/commons/src/constants';
import { conditionSlugForUuid } from '@reverbdotcom/commons/src/condition_helpers';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

import {
  CSP,
  DataServicesPriceGuideToolEstimatesContainer,
  CanonicalProduct,
} from '@reverbdotcom/commons/src/gql/graphql';
import URLHelpers from '../../shared/url_helpers';
import PriceGuideToolEstimates from './price_guide_tool_estimates';
import PriceGuideToolTransactionGraphContainer from './price_guide_tool_transaction_graph_container';
import noResultsImage from '../../../../images/icons/empty-states/not-found.svg';
import { connect } from '@reverbdotcom/commons/src/connect';

export const COMPONENT_NAME = 'PriceGuideToolEstimatesContainer';

const USD = 'USD';

interface IExternalProps extends IUserContext {
  csp: CSP;
  conditionUuid: string;
  canonicalProduct: CanonicalProduct;
  location?: Location;
  updateShowEstimate: (value: boolean) => void;
}

type IProps = ChildProps<IExternalProps, DataServicesPriceGuideToolEstimatesContainer.Query>;

export function PriceEstimateFooter({
  csp,
  canonicalProductId,
  updateShowEstimate,
  conditionSlug,
}) {
  return (
    <div className="d-flex fx-justify-between fx-align-center mobile-fx-dir-col">
      <button type="button" className="button-as-link ph ph-l ph-arrow-left weight-bold mobile-mtb-2" onClick={() => updateShowEstimate(false)}>
        {I18n.t('discovery.priceGuideTool.show.form.backButtonText')}
      </button>

      <div className="d-flex fx-align-center mobile-fx-dir-col mobile-width-100">
        <CoreLink
          className="button button--primary mr-4 mobile-mr-0 mobile-mtb-2 mobile-width-100"
          to={URLHelpers.prefilledListingFromCPPath(canonicalProductId)}
          target="_blank"
          clickEvent={{
            componentName: COMPONENT_NAME,
            eventName: MParticleEventName.ClickedSellYoursButtonFromPriceGuideTool,
            cspId: csp.id,
            cspSlug: csp.slug,
            cpId: canonicalProductId,
            conditionSlug,
            entityId: canonicalProductId,
            entityType: 'url',
          }}
        >
          {I18n.t('discovery.priceGuideTool.show.form.results.sellYoursButtonText')}
        </CoreLink>

        <CoreLink
          className="button button--primary mobile-width-100"
          to={URLHelpers.cspPath(csp.slug)}
          target="_blank"
          clickEvent={{
            componentName: COMPONENT_NAME,
            eventName: MParticleEventName.ClickedViewLiveListingsButtonFromPriceGuideTool,
            cspId: csp.id,
            cspSlug: csp.slug,
            cpId: canonicalProductId,
            conditionSlug,
            entityId: csp.id,
            entityType: 'url',
          }}
        >
          {I18n.t('discovery.priceGuideTool.show.form.results.viewLiveListingsText')}
        </CoreLink>
      </div>

    </div>
  );
}

export function PriceGuideToolEstimatesContainerSubtitle({
  canonicalProduct,
  conditionSlug,
}) {
  return (
    <>
      <I18N
        html
        text="discovery.priceGuideTool.show.form.results.basedOn"
        args={{
          boldOpen: '<b>',
          boldClose: '</b>',
          modelName: canonicalProduct.name,
          condition: I18n.t(`discovery.conditions.${conditionSlug}`),
        }}
      />
    </>
  );
}

export function NoEstimatesContainer({
  canonicalProductId,
  conditionSlug,
  csp,
  updateShowEstimate,
}) {
  return (
    <div className="d-flex fx-dir-col fx-justify-between fx-align-center  scaling-padding-4">
      <p className="mb-0 size-120 mobile-size-100 align-center">
        {I18n.t('discovery.priceGuideTool.show.form.results.noEstimates.title')}
      </p>

      <div className="d-flex fx-wrap mt-8 tablet-mt-4">
        <button
          className="button button--secondary mr-4 mobile-mr-0 mobile-mb-2 mobile-width-100 ph ph-l ph-arrow-left"
          onClick={() => {
            trackEvent({
              componentName: COMPONENT_NAME,
              eventName: MParticleEventName.ClickedBackButtonToPriceGuideToolIndexWhenNoEstimate,
              cspId: csp.id,
              cspSlug: csp.slug,
              cpId: canonicalProductId,
              conditionSlug,
            });
            updateShowEstimate(false);
          }}
        >
          {I18n.t('discovery.priceGuideTool.show.form.results.noEstimates.backButton')}
        </button>

        <CoreLink
          className="button button--primary mobile-width-100"
          to={URLHelpers.cspPath(csp.slug)}
          target="_blank"
          clickEvent={{
            componentName: COMPONENT_NAME,
            eventName: MParticleEventName.ClickedViewLiveListingsFromPriceGuideToolShowWhenNoEstimate,
            cspId: csp.id,
            cspSlug: csp.slug,
            cpId: canonicalProductId,
            conditionSlug,
            entityId: csp.id,
            entityType: 'url',
          }}
        >
          {I18n.t('discovery.priceGuideTool.show.form.results.viewLiveListingsText')}
        </CoreLink>
      </div>

      <div className="width-30 mobile-width-60">
        <img src={noResultsImage} alt="" />
      </div>
    </div>
  );
}


export function PriceGuideToolEstimatesContainer({
  user,
  csp,
  canonicalProduct,
  conditionUuid,
  updateShowEstimate,
  data,
}: IProps) {
  const {
    loading,
    priceRecommendations = {},
  } = data;

  const { priceRecommendations: prices = [] } = priceRecommendations;
  const price = prices[0] || {};
  const {
    priceLow,
    priceMiddle,
    priceHigh,
    priceMiddleThirtyDaysAgo,
  } = price;

  const hasNoPriceEstimates = !priceMiddle;
  const hasInvalidPriceLow = Number(priceLow?.amountCents) <= 0;

  const isUSBasedUser = user.countryCode == US;
  const isViewingCurrencyInUSD = user.currency == USD;
  const displayRegionalEstimateCaveat = !isUSBasedUser || !isViewingCurrencyInUSD;
  const conditionSlug = conditionSlugForUuid(conditionUuid);

  const title = hasNoPriceEstimates ? '' : I18n.t('discovery.priceGuideTool.show.form.results.reverbEstimatedValue');
  const subtitle = hasNoPriceEstimates ? null : <PriceGuideToolEstimatesContainerSubtitle canonicalProduct={canonicalProduct} conditionSlug={conditionSlug} />;
  const footer = hasNoPriceEstimates ? null : <PriceEstimateFooter csp={csp} canonicalProductId={canonicalProduct.id} updateShowEstimate={updateShowEstimate} conditionSlug={conditionSlug} />;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'PriceGuideToolEstimatesContainer',
    cspId: csp.id,
    cspSlug: csp.slug,
    cpId: canonicalProduct.id,
    conditionSlug,
    viewedEstimatedValue: !hasNoPriceEstimates,
  }, !loading);

  if (loading) return null;

  return (
    <div className="price-guide-tool-estimates-container">
      <SiteModule
        footerContent={footer}
        title={title}
        subtitle={subtitle}
      >
        {(hasNoPriceEstimates || hasInvalidPriceLow) ? (
          <NoEstimatesContainer
            canonicalProductId={canonicalProduct.id}
            csp={csp}
            conditionSlug={conditionSlug}
            updateShowEstimate={updateShowEstimate}
          />
        ) : (
          <div className="d-flex tablet-fx-wrap">
            <div className="d-flex width-60 tablet-width-100 bdr-1 bd--offwhite bd--darken tablet-bdr-0 tablet-bdb-1">
              <PriceGuideToolEstimates
                priceLow={priceLow}
                priceMiddle={priceMiddle}
                priceHigh={priceHigh}
                priceMiddleThirtyDaysAgo={priceMiddleThirtyDaysAgo}
                displayRegionalEstimateCaveat={displayRegionalEstimateCaveat}
              />
            </div>

            <PriceGuideToolTransactionGraphContainer
              csp={csp}
              canonicalProductIds={[canonicalProduct.id]}
              conditionUuids={[conditionUuid]}
            />
          </div>
        )}
      </SiteModule>
    </div>

  );
}

const withQuery = withGraphql<IProps, DataServicesPriceGuideToolEstimatesContainer.Query>(
  gql`
    query DataServices_PriceGuideToolEstimatesContainer(
      $priceRecommendationQueries: [Input_reverb_pricing_PriceRecommendationQuery]
    ) {
      priceRecommendations(input: {priceRecommendationQueries: $priceRecommendationQueries}) {
        priceRecommendations {
          priceLow {
            amountCents
            currency
          }
          priceMiddle {
            amountCents
            currency
          }
          priceHigh {
            amountCents
            currency
          }
          priceMiddleThirtyDaysAgo {
            amountCents
            currency
          }
        }
      }
    }
  `,
  {
    options: ({ conditionUuid, canonicalProduct }) => {
      const priceRecommendationQueries = [{
        canonicalProductId: canonicalProduct.id,
        conditionUuid,
        countryCode: US, // For the time being, all estimates will be based on US country code, with disclaimer text for non-US users
      }];

      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: {
          priceRecommendationQueries,
        },
      };
    },
  },
);

export default connect<IExternalProps>([
  withUserContext,
  withQuery,
])(props => {
  return <PriceGuideToolEstimatesContainer {...props} />;
});
